import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageSquarePlus, X, Send, CheckCircle } from 'lucide-react';
import logger from '../utils/logger';
import { useModalGuard, useModalRegistration } from '../contexts/ModalGuardContext';
import useEscapeKey from '../hooks/useEscapeKey';
import useDirtyConfirm from '../hooks/useDirtyConfirm';
import InlineConfirmCloseBar from './ui/InlineConfirmCloseBar';

const FeedbackWidget = () => {
  const [open, setOpen] = useState(false);
  const { guard } = useModalGuard();
  useModalRegistration('feedback-widget', open);
  const [category, setCategory] = useState('suggestion');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const location = useLocation();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Discard guard: prompt before losing the typed feedback.
  const isDirty = description.trim().length > 0 && !success;
  const closeAndReset = () => {
    setOpen(false);
    setDescription('');
    setCategory('suggestion');
    setError('');
  };
  const { showConfirm, requestClose, confirmDiscard, cancelDiscard } = useDirtyConfirm({ onClose: closeAndReset, isDirty });
  useEscapeKey(requestClose, open);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim()) return;
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ category, description: description.trim(), page: location.pathname }),
      });
      if (res.ok) {
        setSuccess(true);
        setDescription('');
        setCategory('suggestion');
        setTimeout(() => { setSuccess(false); setOpen(false); }, 2000);
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to submit');
      }
    } catch (err) {
      logger.error(err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { value: 'suggestion',       label: 'I have a suggestion',      emoji: '💡',
      placeholder: 'Tell us your idea — what would make MyInvestFolio better for you?',
      color: 'bg-purple-500/20 text-purple-300 border-purple-500/40' },
    { value: 'didnt_feel_right', label: "Something didn't feel right", emoji: '🤔',
      placeholder: 'Tell us what you noticed — what happened, and what did you expect instead?',
      color: 'bg-amber-500/20 text-amber-300 border-amber-500/40' },
    { value: 'question',         label: 'I have a question',         emoji: '❓',
      placeholder: 'Ask us anything — we read every message.',
      color: 'bg-blue-500/20 text-blue-300 border-blue-500/40' },
    { value: 'thanks',           label: 'I want to say thanks',      emoji: '💚',
      placeholder: 'Share what you like — every kind word helps us build a better MyInvestFolio.',
      color: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' },
  ];
  const activeCategory = categories.find(c => c.value === category) || categories[0];

  if (success) {
    return (
      <div className="fixed bottom-24 right-6 z-50">
        <div className="bg-gray-800 border border-green-500/40 rounded-2xl shadow-2xl p-6 w-80 text-center">
          <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
          <p className="text-white font-semibold">Thank you!</p>
          <p className="text-gray-400 text-sm mt-1">Your feedback has been submitted.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-24 right-6 z-50">
      {open ? (
        <div className="bg-gray-800 border border-gray-700 rounded-2xl shadow-2xl w-80 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
            <h3 className="text-white font-semibold text-base">Send Feedback</h3>
            <button onClick={requestClose} className="text-gray-400 hover:text-white transition" data-testid="feedback-close" aria-label="Close feedback">
              <X className="w-5 h-5" />
            </button>
          </div>

          {showConfirm && (
            <div className="p-3 border-b border-gray-700">
              <InlineConfirmCloseBar variant="dark" onDiscard={confirmDiscard} onKeep={cancelDiscard} />
            </div>
          )}

          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">What&apos;s on your mind?</label>
              <div className="grid grid-cols-1 gap-1.5">
                {categories.map(c => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setCategory(c.value)}
                    data-testid={`feedback-cat-${c.value}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition text-left ${
                      category === c.value ? c.color : 'bg-gray-700/50 text-gray-400 border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    <span className="text-base" aria-hidden="true">{c.emoji}</span>
                    <span>{c.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Details</label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder={activeCategory.placeholder}
                required
                rows={4}
                data-testid="feedback-description"
                className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-hidden focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none transition"
              />
              <p className="text-[11px] text-gray-500 mt-1.5" data-testid="feedback-page-indicator">
                You&apos;re on: <span className="text-gray-400 font-mono">{location.pathname || '/'}</span>
              </p>
            </div>

            {error && <p className="text-red-400 text-xs">{error}</p>}

            <button
              type="submit"
              disabled={loading || !description.trim()}
              data-testid="feedback-submit"
              className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-40 text-white font-medium py-2.5 rounded-lg transition text-sm"
            >
              <Send className="w-4 h-4" />
              {loading ? 'Sending...' : 'Submit Feedback'}
            </button>
          </form>
        </div>
      ) : (
        <button
          onClick={() => guard(() => setOpen(true), 'dialog')}
          data-testid="feedback-open-btn"
          title="We'd love to hear from you"
          className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-3 rounded-full shadow-lg hover:shadow-xl transition-all"
        >
          <MessageSquarePlus className="w-5 h-5" />
          <span className="font-medium text-sm">Send Feedback</span>
        </button>
      )}
    </div>
  );
};

export default FeedbackWidget;
